import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  EMPTY_FLIPBOOK_ANNOTATION_STATE,
  type FlipbookAnnotationPage,
  type FlipbookAnnotationState,
} from './flipbook-annotation-contracts'
import {
  flipbookAnnotationRepository,
  type FlipbookAnnotationRepository,
  type FlipbookAnnotationIdentity,
} from '@services/firebase/flipbook-annotations'
import type { BookPdfSlot } from '@services/firebase/book-pdf-reader'

export type FlipbookAnnotationSyncStatus = 'idle' | 'loading' | 'saving' | 'saved' | 'error'

interface UseFlipbookAnnotationPersistenceOptions {
  readonly bookId: string | null
  readonly revision: string | null
  readonly slot: BookPdfSlot
  readonly repository?: FlipbookAnnotationRepository
}

interface FlipbookAnnotationPersistence {
  readonly annotationHydrationKey: string
  readonly hydratedAnnotationState: FlipbookAnnotationState | null
  readonly isAnnotationHydrationReady: boolean
  readonly onAnnotationStateChange: (state: FlipbookAnnotationState) => void
  readonly retryAnnotationSync: () => void
  readonly syncStatus: FlipbookAnnotationSyncStatus
}

const SAVE_DEBOUNCE_MS = 700
const RETRY_BASE_DELAY_MS = 1000
const RETRY_MAX_DELAY_MS = 15000

interface PendingAnnotationWrite {
  readonly identity: FlipbookAnnotationIdentity
  readonly identityKey: string
  readonly page: FlipbookAnnotationPage
}

function getIdentityKey(identity: FlipbookAnnotationIdentity | null) {
  return identity ? `${identity.bookId}:${identity.slot}:${identity.revision}` : 'none'
}

function getPage(state: FlipbookAnnotationState, page: number): FlipbookAnnotationPage {
  return {
    page,
    strokes: state.strokesByPage[page] ?? [],
    texts: state.textsByPage[page] ?? [],
  }
}

function getPageSignature(page: FlipbookAnnotationPage) {
  return JSON.stringify(page)
}

function getChangedPages(previous: FlipbookAnnotationState, next: FlipbookAnnotationState) {
  const pageNumbers = new Set([
    ...Object.keys(previous.strokesByPage).map(Number),
    ...Object.keys(previous.textsByPage).map(Number),
    ...Object.keys(next.strokesByPage).map(Number),
    ...Object.keys(next.textsByPage).map(Number),
  ])

  return [...pageNumbers]
    .filter((page) => getPageSignature(getPage(previous, page)) !== getPageSignature(getPage(next, page)))
    .map((page) => getPage(next, page))
}

export function useFlipbookAnnotationPersistence({
  bookId,
  repository = flipbookAnnotationRepository,
  revision,
  slot,
}: UseFlipbookAnnotationPersistenceOptions): FlipbookAnnotationPersistence {
  const identity = useMemo<FlipbookAnnotationIdentity | null>(() => (
    bookId && revision ? { bookId, revision, slot } : null
  ), [bookId, revision, slot])
  const identityKey = getIdentityKey(identity)
  const identityRef = useRef(identity)
  const generationRef = useRef(0)
  const isMountedRef = useRef(false)
  const isHydratedRef = useRef(false)
  const flushPendingRef = useRef<(generation?: number) => Promise<void>>(async () => undefined)
  const latestStateRef = useRef<FlipbookAnnotationState>(EMPTY_FLIPBOOK_ANNOTATION_STATE)
  const pendingWritesRef = useRef(new Map<string, PendingAnnotationWrite>())
  const saveWorkersRef = useRef(new Map<string, Promise<void>>())
  const retryAttemptsRef = useRef(new Map<string, number>())
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [hydratedAnnotationState, setHydratedAnnotationState] = useState<FlipbookAnnotationState>(EMPTY_FLIPBOOK_ANNOTATION_STATE)
  const [hydratedStateKey, setHydratedStateKey] = useState('none')
  const [syncStatus, setSyncStatus] = useState<FlipbookAnnotationSyncStatus>('idle')

  const setSyncStatusIfCurrent = useCallback((status: FlipbookAnnotationSyncStatus, generation: number, expectedIdentityKey?: string) => {
    if (
      isMountedRef.current &&
      generationRef.current === generation &&
      (!expectedIdentityKey || getIdentityKey(identityRef.current) === expectedIdentityKey)
    ) {
      setSyncStatus(status)
    }
  }, [])

  const scheduleRetry = useCallback((delayMs: number) => {
    if (retryTimerRef.current) {
      return
    }

    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null
      void flushPendingRef.current(generationRef.current)
    }, delayMs)
  }, [])

  const savePendingWrite = useCallback((write: PendingAnnotationWrite, generation: number): Promise<void> => {
    const existingWorker = saveWorkersRef.current.get(write.identityKey)
    if (existingWorker) {
      return existingWorker
    }

    const worker = (async () => {
      while (true) {
        const latestWrite = pendingWritesRef.current.get(write.identityKey)
        if (!latestWrite) {
          return
        }

        // Remove only the snapshot being sent. If a newer edit arrives while this
        // request is in flight it stays in the queue and is sent next, in order.
        pendingWritesRef.current.delete(write.identityKey)
        try {
          await repository.savePage({ identity: latestWrite.identity, page: latestWrite.page })
          retryAttemptsRef.current.delete(write.identityKey)
          if (!pendingWritesRef.current.has(write.identityKey)) {
            setSyncStatusIfCurrent('saved', generation, getIdentityKey(latestWrite.identity))
            return
          }
        } catch {
          if (!pendingWritesRef.current.has(write.identityKey)) {
            pendingWritesRef.current.set(write.identityKey, latestWrite)
          }
          const attempts = (retryAttemptsRef.current.get(write.identityKey) ?? 0) + 1
          retryAttemptsRef.current.set(write.identityKey, attempts)
          const delayMs = Math.min(RETRY_MAX_DELAY_MS, RETRY_BASE_DELAY_MS * (2 ** Math.min(attempts - 1, 4)))
          setSyncStatusIfCurrent('error', generation, getIdentityKey(latestWrite.identity))
          scheduleRetry(delayMs)
          return
        }
      }
    })()

    saveWorkersRef.current.set(write.identityKey, worker)
    worker.finally(() => {
      if (saveWorkersRef.current.get(write.identityKey) === worker) {
        saveWorkersRef.current.delete(write.identityKey)
      }
    }).catch(() => undefined)
    return worker
  }, [repository, scheduleRetry, setSyncStatusIfCurrent])

  const flushPending = useCallback(async (generation = generationRef.current) => {
    const pendingWrites = [...pendingWritesRef.current.values()]
    if (pendingWrites.length === 0) {
      return
    }

    const activeIdentityKey = getIdentityKey(identityRef.current)
    if (pendingWrites.some((write) => getIdentityKey(write.identity) === activeIdentityKey)) {
      setSyncStatusIfCurrent('saving', generation, activeIdentityKey)
    }
    await Promise.allSettled(pendingWrites.map((write) => savePendingWrite(write, generation)))
  }, [savePendingWrite, setSyncStatusIfCurrent])

  useEffect(() => {
    flushPendingRef.current = flushPending
  }, [flushPending])

  const scheduleFlush = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
    }
    const generation = generationRef.current
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null
      void flushPendingRef.current(generation)
    }, SAVE_DEBOUNCE_MS)
  }, [])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
        saveTimerRef.current = null
      }
      void flushPendingRef.current(generationRef.current)
    }
  }, [])

  useEffect(() => {
    generationRef.current += 1
    const generation = generationRef.current
    identityRef.current = identity
    isHydratedRef.current = false
    latestStateRef.current = EMPTY_FLIPBOOK_ANNOTATION_STATE
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
    queueMicrotask(() => {
      if (generationRef.current === generation && identity) {
        setSyncStatus('loading')
      }
    })

    if (!identity) {
      queueMicrotask(() => {
        if (generationRef.current === generation) {
          setHydratedAnnotationState(EMPTY_FLIPBOOK_ANNOTATION_STATE)
          setHydratedStateKey(identityKey)
          setSyncStatus('idle')
        }
      })
      return undefined
    }

    let isCancelled = false
    void repository.load(identity)
      .then((snapshot) => {
        if (isCancelled || generationRef.current !== generation) {
          return
        }
        latestStateRef.current = snapshot.state
        isHydratedRef.current = true
        setHydratedAnnotationState(snapshot.state)
        setHydratedStateKey(identityKey)
        setSyncStatus('saved')
      })
      .catch(() => {
        if (isCancelled || generationRef.current !== generation) {
          return
        }
        latestStateRef.current = EMPTY_FLIPBOOK_ANNOTATION_STATE
        isHydratedRef.current = true
        setHydratedAnnotationState(EMPTY_FLIPBOOK_ANNOTATION_STATE)
        setHydratedStateKey(identityKey)
        setSyncStatus('error')
      })

    return () => {
      isCancelled = true
      void flushPendingRef.current(generation)
    }
  }, [identity, identityKey, repository])

  const onAnnotationStateChange = useCallback((nextState: FlipbookAnnotationState) => {
    const activeIdentity = identityRef.current
    if (!activeIdentity || !isHydratedRef.current) {
      return
    }

    const changedPages = getChangedPages(latestStateRef.current, nextState)
    latestStateRef.current = nextState
    changedPages.forEach((page) => {
      const writeKey = `${getIdentityKey(activeIdentity)}:${page.page}`
      pendingWritesRef.current.set(writeKey, {
        identity: activeIdentity,
        identityKey: writeKey,
        page,
      })
    })
    if (changedPages.length > 0) {
      setSyncStatus('saving')
      scheduleFlush()
    }
  }, [scheduleFlush])

  const retryAnnotationSync = useCallback(() => {
    const activeIdentity = identityRef.current
    const activeIdentityKey = getIdentityKey(activeIdentity)
    if ([...pendingWritesRef.current.values()].some((write) => getIdentityKey(write.identity) === activeIdentityKey)) {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
      void flushPendingRef.current(generationRef.current)
      return
    }
    if (!activeIdentity) {
      return
    }
    generationRef.current += 1
    setSyncStatus('loading')
    const generation = generationRef.current
    void repository.load(activeIdentity)
      .then((snapshot) => {
        if (generationRef.current !== generation) return
        latestStateRef.current = snapshot.state
        isHydratedRef.current = true
        setHydratedAnnotationState(snapshot.state)
        setHydratedStateKey(identityKey)
        setSyncStatus('saved')
      })
      .catch(() => {
        if (generationRef.current === generation) setSyncStatus('error')
      })
  }, [identityKey, repository])

  useEffect(() => {
    const handleOnline = () => {
      if (pendingWritesRef.current.size > 0) {
        void flushPendingRef.current(generationRef.current)
      }
    }

    window.addEventListener('online', handleOnline)
    return () => window.removeEventListener('online', handleOnline)
  }, [])

  return {
    annotationHydrationKey: identityKey,
    hydratedAnnotationState,
    isAnnotationHydrationReady: hydratedStateKey === identityKey,
    onAnnotationStateChange,
    retryAnnotationSync,
    syncStatus,
  }
}
